/**
 * SEO Utility - Centralized utility for SEO management
 *
 * Features:
 * - Dynamic document title updates
 * - Meta description and keywords management
 * - Open Graph tag updates for social sharing
 * - Twitter Card tag updates
 * - Language-aware SEO (EN/AR support)
 * - No external dependencies - uses native DOM APIs
 *
 * Usage:
 * SEOUtil.updatePageSEO({
 *   title: 'Page Title',
 *   description: 'Page description',
 *   keywords: ['keyword1', 'keyword2'],
 *   path: '/page-path'
 * });
 *
 * SEOUtil.setLanguage('ar'); // Updates html lang and dir
 */

import { Language } from '../constants/language';

export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string[];
  image?: string;
  path?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
}

export interface SEOPageConfig {
  [key: string]: {
    en: SEOConfig;
    ar: SEOConfig;
  };
}

export class SEOUtil {
  private static readonly BASE_URL = 'https://zoeforallnations.com';
  private static readonly DEFAULT_IMAGE = '/og-image.jpg';
  private static readonly SITE_NAME_EN = 'ZOE For All Nations';
  private static readonly SITE_NAME_AR = 'زوى لكل الأمم';

  /**
   * Default SEO configuration for all pages
   */
  public static readonly PAGE_SEO: SEOPageConfig = {
    home: {
      en: {
        title: 'ZOE For All Nations',
        description: 'ZOE For All Nations empowers ministries, schools, events, and kingdom initiatives to impact society with faith, integrity, and excellence.',
        keywords: ['ZOE', 'Zoe For All Nations', 'Christian ministries', 'Kingdom projects', 'Faith-based organizations', 'Christian education', 'Christian events', 'Biblical teaching', 'Gospel mission'],
        path: '/'
      },
      ar: {
        title: 'زوى لكل الأمم',
        description: 'زوى لكل الأمم تمكّن الخدمات والمدارس والفعاليات ومبادرات الملكوت للتأثير في المجتمع بالإيمان والنزاهة والتميز.',
        keywords: ['زوى', 'زوى لكل الأمم', 'خدمات مسيحية', 'مشاريع الملكوت', 'منظمات قائمة على الإيمان', 'تعليم مسيحي', 'فعاليات مسيحية', 'تعليم كتابي', 'حياة لكل الامم'],
        path: '/'
      }
    },
    ministries: {
      en: {
        title: 'Christian Ministries Empowering Faith, Leadership & Service | ZOE',
        description: 'Discover ZOE ministries including 7 Weeks Harvest, Chosen Generation youth meetings, Goodness and Mercy, Journey discipleship groups, and Encounter Worship. Join transformative Christian ministry programs.',
        keywords: ['Christian ministry', 'Church missions', 'Faith outreach', 'Gospel service', 'Kingdom leadership', '7 Weeks', '7 Weeks Harvest', 'Harvest Program', 'Discipleship Groups', 'Journey', 'Goodness and Mercy', 'Youth Meeting', 'Chosen Generation', 'Encounter Worship', 'Light to the World'],
        path: '/ministries'
      },
      ar: {
        title: 'خدمات مسيحية لتمكين الإيمان والقيادة والخدمة | زوى',
        description: 'اكتشف خدمات زوى بما في ذلك 7 أسابيع حصاد، اجتماع الشباب تشوزين، خير ورحمة، مجموعات تلمذة رحلة، وعبادة اللقاء. انضم لبرامج الخدمة المسيحية التحويلية.',
        keywords: ['خدمة مسيحية', 'مهمة الكنيسة', 'خدمة الإيمان', 'خدمة الإنجيل', 'قيادة الملكوت', '7 اسابيع', '7 اسابيع حصاد', 'برنامج الحصاد', 'مجموعات التلمذة', 'رحله', 'خير ورحمة', 'اجتماع الشباب', 'تشوزين', 'عبادة اللقاء', 'نور للعالم'],
        path: '/ministries'
      }
    },
    events: {
      en: {
        title: 'Christian Events, Conferences & Gatherings for All Nations | ZOE',
        description: 'Join ZOE Christian conferences, worship events, faith gatherings, leadership summits, and youth conferences. Experience transformative Christian events in Egypt and beyond.',
        keywords: ['Christian conferences', 'Worship events', 'Faith gatherings', 'Leadership summits', 'Youth conferences', 'Christian events Egypt', 'Ministry conferences', 'Church gatherings', 'Worship nights', 'Prayer gatherings'],
        path: '/events'
      },
      ar: {
        title: 'مؤتمرات وفعاليات مسيحية لجميع الأمم | زوى',
        description: 'انضم لمؤتمرات زوى المسيحية وفعاليات العبادة وتجمعات الإيمان وقمم القيادة ومؤتمرات الشباب. اختبر فعاليات مسيحية تحويلية في مصر وما بعدها.',
        keywords: ['مؤتمرات مسيحية', 'فعاليات عبادة', 'تجمعات إيمان', 'قمم قيادة', 'مؤتمرات شباب', 'فعاليات مسيحية مصر', 'مؤتمرات خدمة', 'تجمعات الكنيسة', 'ليالي عبادة', 'تجمعات صلاة'],
        path: '/events'
      }
    },
    schools: {
      en: {
        title: 'Christian Schools & Training Programs Rooted in Biblical Values | ZOE',
        description: 'Enroll in ZOE Schools including SNL - Super Natural Life Style training. Transformative Christian education programs for discipleship training, biblical studies, and ministry development.',
        keywords: ['Christian schools', 'Biblical education', 'Discipleship training', 'Christian curriculum', 'Kingdom learning', 'SNL', 'Super Natural Life Style', 'Ministry training', 'Christian education programs', 'Biblical studies', 'Theological education'],
        path: '/schools'
      },
      ar: {
        title: 'مدارس وبرامج تعليم مسيحية قائمة على القيم الكتابية | زوى',
        description: 'سجّل في مدارس زوى بما في ذلك تدريب اس ان ال - نمط الحياة الفائق الطبيعي. برامج تعليم مسيحي تحويلية للتلمذة والدراسات الكتابية وتطوير الخدمة.',
        keywords: ['مدارس مسيحية', 'تعليم كتابي', 'تدريب التلمذة', 'منهج مسيحي', 'تعلم الملكوت', 'اس ان ال', 'نمط الحياة الفائق', 'تدريب الخدمة', 'برامج التعليم المسيحي', 'الدراسات الكتابية', 'التعليم اللاهوتي'],
        path: '/schools'
      }
    },
    store: {
      en: {
        title: 'Faith-Based Resources, Courses & Kingdom Tools | ZOE Store',
        description: 'Explore ZOE Store for Christian books, courses, devotionals, study guides, and ministry resources. Faith-based tools for spiritual growth and kingdom building.',
        keywords: ['Christian courses', 'Faith-based tools', 'Ministry resources', 'Biblical study materials', 'Online Christian programs', 'Christian books', 'Spiritual resources', 'Ministry tools', 'Christian devotionals', 'Study guides'],
        path: '/store'
      },
      ar: {
        title: 'منتجات وموارد مسيحية لبناء الملكوت | متجر زوى',
        description: 'اكتشف متجر زوى للكتب والدورات والتأملات وأدلة الدراسة وموارد الخدمة المسيحية. أدوات قائمة على الإيمان للنمو الروحي وبناء الملكوت.',
        keywords: ['دورات مسيحية', 'أدوات قائمة على الإيمان', 'موارد خدمة', 'مواد دراسة كتابية', 'برامج مسيحية عبر الإنترنت', 'كتب مسيحية', 'موارد روحية', 'أدوات خدمة', 'تأملات مسيحية', 'أدلة دراسة'],
        path: '/store'
      }
    },
    about: {
      en: {
        title: 'About ZOE For All Nations | Our Vision & Mission',
        description: 'Learn about ZOE For All Nations - an evangelical Christian ministry from Egypt sharing the divine life (Zoe) with all nations. Discover our vision, values, and kingdom impact.',
        keywords: ['About ZOE', 'ZOE ministry', 'Christian ministry Egypt', 'Divine life', 'Zoe life', 'Gospel mission', 'Kingdom impact', 'Egyptian Christian ministry'],
        path: '/about'
      },
      ar: {
        title: 'عن زوى لكل الأمم | رؤيتنا ورسالتنا',
        description: 'تعرّف على زوى لكل الأمم - خدمة مسيحية إنجيلية من مصر تشارك الحياة الإلهية (زوى) مع جميع الأمم. اكتشف رؤيتنا وقيمنا وتأثيرنا في الملكوت.',
        keywords: ['عن زوى', 'خدمة زوى', 'خدمة مسيحية مصر', 'الحياة الإلهية', 'حياة زوى', 'مهمة الإنجيل', 'تأثير الملكوت', 'خدمة مسيحية مصرية'],
        path: '/about'
      }
    }
  };

  /**
   * Get or create a meta tag
   */
  private static getOrCreateMetaTag(selector: string, createAttrs: Record<string, string>): HTMLMetaElement {
    let element = document.querySelector<HTMLMetaElement>(selector);
    if (!element) {
      element = document.createElement('meta');
      Object.entries(createAttrs).forEach(([key, value]) => {
        element!.setAttribute(key, value);
      });
      document.head.appendChild(element);
    }
    return element;
  }

  /**
   * Update a meta tag content
   */
  private static updateMetaTag(selector: string, content: string, createAttrs: Record<string, string>): void {
    const element = this.getOrCreateMetaTag(selector, createAttrs);
    element.setAttribute('content', content);
  }

  /**
   * Get or create a link tag
   */
  private static getOrCreateLinkTag(selector: string, createAttrs: Record<string, string>): HTMLLinkElement {
    let element = document.querySelector<HTMLLinkElement>(selector);
    if (!element) {
      element = document.createElement('link');
      Object.entries(createAttrs).forEach(([key, value]) => {
        element!.setAttribute(key, value);
      });
      document.head.appendChild(element);
    }
    return element;
  }

  /**
   * Update document title
   */
  public static setTitle(title: string): void {
    document.title = title;
    this.updateMetaTag('meta[property="og:title"]', title, { property: 'og:title' });
    this.updateMetaTag('meta[name="twitter:title"]', title, { name: 'twitter:title' });
  }

  /**
   * Update meta description
   */
  public static setDescription(description: string): void {
    this.updateMetaTag('meta[name="description"]', description, { name: 'description' });
    this.updateMetaTag('meta[property="og:description"]', description, { property: 'og:description' });
    this.updateMetaTag('meta[name="twitter:description"]', description, { name: 'twitter:description' });
  }

  /**
   * Update meta keywords
   */
  public static setKeywords(keywords: string[]): void {
    this.updateMetaTag('meta[name="keywords"]', keywords.join(', '), { name: 'keywords' });
  }

  /**
   * Update Open Graph image
   */
  public static setImage(imageUrl: string): void {
    const absoluteUrl = imageUrl.startsWith('http') ? imageUrl : `${this.BASE_URL}${imageUrl}`;
    this.updateMetaTag('meta[property="og:image"]', absoluteUrl, { property: 'og:image' });
    this.updateMetaTag('meta[name="twitter:image"]', absoluteUrl, { name: 'twitter:image' });
  }

  /**
   * Update canonical URL
   */
  public static setCanonicalUrl(path: string): void {
    const url = `${this.BASE_URL}${path}`;
    const link = this.getOrCreateLinkTag('link[rel="canonical"]', { rel: 'canonical' });
    link.setAttribute('href', url);

    this.updateMetaTag('meta[property="og:url"]', url, { property: 'og:url' });
    this.updateMetaTag('meta[name="twitter:url"]', url, { name: 'twitter:url' });
  }

  /**
   * Update Open Graph type
   */
  public static setOgType(type: 'website' | 'article'): void {
    this.updateMetaTag('meta[property="og:type"]', type, { property: 'og:type' });
  }

  /**
   * Set robots meta tag
   */
  public static setRobots(index: boolean = true): void {
    const content = index ? 'index, follow' : 'noindex, nofollow';
    this.updateMetaTag('meta[name="robots"]', content, { name: 'robots' });
  }

  /**
   * Update HTML language and direction attributes
   */
  public static setLanguage(language: Language): void {
    const isArabic = language === Language.AR;
    document.documentElement.lang = language;
    document.documentElement.dir = isArabic ? 'rtl' : 'ltr';

    // Update og:locale
    const locale = isArabic ? 'ar_EG' : 'en_US';
    this.updateMetaTag('meta[property="og:locale"]', locale, { property: 'og:locale' });

    // Update site name based on language
    const siteName = isArabic ? this.SITE_NAME_AR : this.SITE_NAME_EN;
    this.updateMetaTag('meta[property="og:site_name"]', siteName, { property: 'og:site_name' });
  }

  /**
   * Update all SEO tags for a page
   */
  public static updatePageSEO(config: SEOConfig): void {
    this.setTitle(config.title);
    this.setDescription(config.description);

    if (config.keywords && config.keywords.length > 0) {
      this.setKeywords(config.keywords);
    }

    if (config.image) {
      this.setImage(config.image);
    }

    if (config.path) {
      this.setCanonicalUrl(config.path);
    }

    if (config.type) {
      this.setOgType(config.type);
    }

    this.setRobots(!config.noIndex);
  }

  /**
   * Get SEO config for a specific page and language
   */
  public static getPageSEO(pageKey: string, language: Language): SEOConfig | null {
    const pageSEO = this.PAGE_SEO[pageKey];
    if (!pageSEO) {
      return null;
    }
    return language === Language.AR ? pageSEO.ar : pageSEO.en;
  }

  /**
   * Apply SEO for a specific page
   */
  public static applyPageSEO(pageKey: string, language: Language): void {
    const config = this.getPageSEO(pageKey, language);
    if (config) {
      this.updatePageSEO(config);
      this.setLanguage(language);
    }
  }

  /**
   * Set custom page SEO (for dynamic content like ministry details, event details, etc.)
   */
  public static setCustomSEO(
    title: string,
    description: string,
    language: Language,
    options?: {
      keywords?: string[];
      image?: string;
      path?: string;
      type?: 'website' | 'article';
    }
  ): void {
    this.updatePageSEO({
      title,
      description,
      keywords: options?.keywords,
      image: options?.image || this.DEFAULT_IMAGE,
      path: options?.path,
      type: options?.type || 'website'
    });
    this.setLanguage(language);
  }

  /**
   * Reset to default home page SEO
   */
  public static resetToDefault(language: Language = Language.EN): void {
    this.applyPageSEO('home', language);
  }
}

export default SEOUtil;
